/* eslint-disable max-len */
import { BehaviorSubject } from 'rxjs';
import { Asset, getAssetById, getAssetDetailSubject, updateAssetDetail } from '../../../store/Asset';
import { DrawerData } from '../../../store/Drawer';
import {
  getProvisionalAssetById,
  getProvisionalAssetDetailSubject,
  ProvisionalAsset
} from '../../../store/ProvisionalAsset';
import { getRunTotalAuditById, getRunTotalAuditDetailSubject } from '../../../store/RunTotalAudit';
import { getRpmAccountDetailSubject } from '../../../store/RpmAccount';
import { deleteSupplierMap } from '../../../store/SupplierMap';
import { deleteApMap } from '../../../store/ApMap';
import {
  deleteLink,
  getLinkById,
  getLinkDetailSubject,
  Link as TallyLink,
  updateLinkDetail
} from '../../../store/Link';
import { Text } from '../../Atoms/Text';
import { deleteProductMap } from '../../../store/ProductMap';
import { deleteCustomerMap, getCustomerMapDetailSubject } from '../../../store/CustomerMap';
import { getFormattedDate } from '../../../utils/helpers';
import {
  deletePaymentById,
  getPaymentById,
  getPaymentDetailSubject,
  Payment,
  updatePaymentDetail
} from '../../../store/Payment';
import {
  getPaymentRecipientDetailSubject,
  PaymentRecipient,
  removePaymentRecipient
} from '../../../store/PaymentRecipient';
import { Correction, removeCorrection } from '../../../store/Correction';
import { getAdjustmentSubject, getAdjustmentById } from '../../../store/Adjustment';
import { RunStatus } from '../../../store/Run';
import { getTicketDetail, getTicketDetailSubject, updateTicketDetail } from '../../../store/Ticket';

export enum ComponentName {
  ASSET = 'asset',
  PROVISIONAL_ASSET = 'provisionalAsset',
  USER = 'user',
  RPM_ACCOUNT = 'rpmAccount',
  SUPPLIER_MAP = 'supplierMap',
  AP_MAP = 'apMap',
  LINK = 'link',
  RUN_TOTAL_AUDIT = 'runTotalAudit',
  PRODUCT_MAP = 'productMap',
  CUSTOMER_MAP = 'customerMap',
  PAYMENT = 'payment',
  PAYMENT_RECIPIENT = 'paymentRecipient',
  CORRECTION = 'correction',
  ADJUSTMENT = 'adjustment',
  TICKET = 'ticket'
}

export interface Link {
  url: string;
  display: string;
}

export interface DetailsMeta {
  title: string;
  subtitleKey?: string;
  componentName: ComponentName;
  idKey?: string;
  error?: string;
  link?: string;
  linkGroup?: Link[];
  showIcon?: boolean;
  replace?: boolean;
  dataSubject?: () => BehaviorSubject<DrawerData | undefined>;
  showExpand?: boolean;
  showArchived?: boolean;
  fullPageLink?: string;
  fetchFunction?: () => Promise<DrawerData | undefined>;
  deleteAction?: () => void;
  updateSubject?: (data: DrawerData) => void;
}

export interface getMetaDataParams {
  name: ComponentName;
  id: string;
  sfId?: string;
  data?: DrawerData;
  replace?: boolean;
}

export const getMetaData = ({ name, id, sfId, data, replace = false }: getMetaDataParams): DetailsMeta => {
  switch (name) {
    case ComponentName.USER:
      return {
        title: 'User Details',
        subtitleKey: 'lastName',
        componentName: ComponentName.USER,
        replace: true
      };
    case ComponentName.LINK:
      return {
        title: 'Link',
        subtitleKey: 'id',
        componentName: ComponentName.LINK,
        showExpand: true,
        replace: false,
        deleteAction: () => deleteLink(id),
        fullPageLink: `/link/${id}`,
        showArchived: data ? (data as TallyLink).archived || false : false,
        fetchFunction: () => getLinkById(id),
        dataSubject: getLinkDetailSubject,
        updateSubject: updateLinkDetail
      };
    case ComponentName.ASSET: {
      const astData = data as Asset;

      return {
        title: 'Asset',
        subtitleKey: 'number',
        componentName: ComponentName.ASSET,
        link: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Asset/${sfId}/view`,
        linkGroup: [
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Contract/${astData?.contractId}/view`,
            display: astData?.contractNumber ? `Contract ${astData?.contractNumber}` : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Contract/${astData?.contractId}/related/CombinedAttachments/view`,
            display: astData?.contractId ? 'Notes & Attachments' : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/ServiceContract/${astData?.serviceContractSFID}/view`,
            display: astData?.serviceContractSFID ? 'Service Contract' : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${astData?.locationSFID}/view`,
            display: astData?.locationSFID ? 'Install Location' : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Opportunity/${astData?.opportunitySFID}/view`,
            display: astData?.opportunitySFID ? 'Opportunity' : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Quote/${astData?.quoteSFID}/view`,
            display: astData?.quoteSFID ? 'Quote' : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Project__c/${astData?.projectSFID}/view`,
            display: astData?.projectSFID ? `Project ${astData?.projectNumber}` : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${astData?.supplierSFID}/view`,
            display: astData?.supplierSFID ? `${astData?.supplierName ?? 'Supplier Missing'}` : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${astData?.tsdSFID}/view`,
            display: astData?.tsdSFID ? `${astData?.tsd ?? 'TSD Missing'}` : ''
          },
          {
            url: `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Account/${astData?.salesAgentSFID}/view`,
            display: astData?.salesAgentSFID ? `${astData?.salesAgent ?? 'Sales Agent Missing'}` : ''
          }
        ],
        showIcon: true,
        replace: replace,
        showExpand: true,
        fullPageLink: `/asset/${id}`,
        fetchFunction: () => getAssetById(id),
        dataSubject: getAssetDetailSubject,
        updateSubject: updateAssetDetail
      };
    }
    case ComponentName.PROVISIONAL_ASSET:
      return {
        title: 'Provisional Asset',
        subtitleKey: 'assetNumber',
        componentName: ComponentName.PROVISIONAL_ASSET,
        showIcon: true,
        replace: false,
        showExpand: true,
        fullPageLink: `/provisional-asset/${id}`,
        showArchived: data ? !(data as ProvisionalAsset).active || false : false,
        fetchFunction: () => getProvisionalAssetById(id),
        dataSubject: getProvisionalAssetDetailSubject
      };
    case ComponentName.RUN_TOTAL_AUDIT:
      return {
        title: 'Run Total Audit',
        subtitleKey: 'createdAt',
        componentName: ComponentName.RUN_TOTAL_AUDIT,
        showIcon: false,
        replace: true,
        showExpand: false,
        fetchFunction: () => getRunTotalAuditById(id),
        dataSubject: getRunTotalAuditDetailSubject
      };
    case ComponentName.RPM_ACCOUNT:
      return {
        title: 'RPM Account',
        subtitleKey: 'accountNumber',
        componentName: ComponentName.RPM_ACCOUNT,
        idKey: 'accountId',
        link: `${process.env.REACT_APP_RPM_URL}/rpm/Page/CustomerAccount.aspx?Item=${id}`,
        showIcon: true,
        replace: false,
        showExpand: true,
        fullPageLink: `/rpm-account/${id}`,
        dataSubject: getRpmAccountDetailSubject
      };
    case ComponentName.SUPPLIER_MAP:
      return {
        title: 'Supplier Mapping',
        subtitleKey: 'salesforce_supplier_name',
        componentName: ComponentName.SUPPLIER_MAP,
        showIcon: true,
        replace: true,
        showExpand: false,
        deleteAction: () => deleteSupplierMap(id)
      };
    case ComponentName.AP_MAP:
      return {
        title: 'AP Mapping',
        subtitleKey: 'ap_name',
        componentName: ComponentName.AP_MAP,
        showIcon: false,
        replace: true,
        showExpand: false,
        deleteAction: () => deleteApMap(id)
      };
    case ComponentName.PRODUCT_MAP:
      return {
        title: 'Product Mapping',
        componentName: ComponentName.PRODUCT_MAP,
        showIcon: false,
        replace: false,
        showExpand: false,
        deleteAction: () => deleteProductMap(id)
      };
    case ComponentName.CUSTOMER_MAP:
      return {
        title: 'Customer Mapping',
        componentName: ComponentName.CUSTOMER_MAP,
        idKey: 'customerId',
        showIcon: false,
        replace: false,
        showExpand: false,
        deleteAction: () => deleteCustomerMap(+id),
        dataSubject: getCustomerMapDetailSubject
      };
    case ComponentName.PAYMENT:
      return {
        title: 'Payment',
        componentName: ComponentName.PAYMENT,
        subtitleKey: 'date',
        replace: false,
        showExpand: false,
        deleteAction: () => deletePaymentById(data as Payment),
        dataSubject: getPaymentDetailSubject,
        fetchFunction: () => getPaymentById(id),
        updateSubject: updatePaymentDetail
      };
    case ComponentName.PAYMENT_RECIPIENT:
      return {
        title: 'Payment Recipient',
        componentName: ComponentName.PAYMENT_RECIPIENT,
        idKey: 'id',
        subtitleKey: 'name',
        replace: true,
        showExpand: false,
        dataSubject: getPaymentRecipientDetailSubject,
        deleteAction: (data as PaymentRecipient)?.canBeDeleted ? () => removePaymentRecipient(id) : undefined
      };
    case ComponentName.CORRECTION:
      return {
        title: 'Correction',
        componentName: ComponentName.CORRECTION,
        subtitleKey: 'combinedAssetNumber',
        replace: false,
        showExpand: false,
        deleteAction:
          (data as Correction)?.paidInRunStatus === RunStatus.PROCESSING
            ? () => removeCorrection(data as Correction)
            : undefined
      };
    case ComponentName.ADJUSTMENT:
      return {
        title: 'Adjustment',
        componentName: ComponentName.ADJUSTMENT,
        subtitleKey: 'commissionAdjustmentId',
        replace: false,
        showExpand: false,
        dataSubject: getAdjustmentSubject,
        fetchFunction: () => getAdjustmentById(Number(id))
      };
    case ComponentName.TICKET:
      return {
        title: 'Ticket',
        componentName: ComponentName.TICKET,
        idKey: 'id',
        subtitleKey: 'number',
        replace: false,
        showExpand: true,
        fullPageLink: `/ticket/${id}`,
        fetchFunction: () => getTicketDetail(id),
        dataSubject: getTicketDetailSubject,
        updateSubject: updateTicketDetail,
        showIcon: true
      };
  }
};

export const subTitle = (componentName: ComponentName, data: DrawerData, subtitleKey: string) => {
  switch (componentName) {
    case ComponentName.USER: {
      return (
        <Text
          color="grey9"
          size="lg">
          {`${data['firstName' as keyof DrawerData] || ''} ${data['lastName' as keyof DrawerData] || ''}`}
        </Text>
      );
    }
    case ComponentName.LINK: {
      return (
        <Text
          isSpan={true}
          size="lg">
          {data['archived' as keyof DrawerData]
            ? 'Archived'
            : data['candidate' as keyof DrawerData]
              ? 'Candidate'
              : 'Active'}{' '}
          Link
        </Text>
      );
    }
    case ComponentName.RUN_TOTAL_AUDIT:
    case ComponentName.PAYMENT: {
      const formatDate = componentName === ComponentName.RUN_TOTAL_AUDIT ? 'lll' : undefined;
      return (
        <Text
          isSpan={true}
          size="lg">
          {getFormattedDate(data?.[subtitleKey as keyof DrawerData] || '', formatDate)}
        </Text>
      );
    }
    case ComponentName.ADJUSTMENT:
      return (
        <Text
          isSpan={true}
          size="lg">
          {`Adj - ${String(data?.[subtitleKey as keyof DrawerData] || '')}`}
        </Text>
      );
    default:
      return (
        <Text
          isSpan={true}
          size="lg">
          {data?.[subtitleKey as keyof DrawerData] || ''}
        </Text>
      );
  }
};

export const getTitle = (data: DrawerData, titleKey?: string) => {
  return titleKey ? String(data[titleKey as keyof DrawerData]) : '';
};
